import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Upload, Trash2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { WaSendBox } from "@/components/wa/WaSendBox";
import { WaHistory } from "@/components/wa/WaHistory";
import { WaStatus } from "@/components/wa/WaStatus";

export default function WhatsAppGroups() {
  const { user } = useAuth();
  const [data, setData] = useState({ groups: [], whatsapp_ready: true });
  const [key, setKey] = useState("jobs");
  const [nums, setNums] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [templates, setTemplates] = useState([]);

  const loadGroups = useCallback(() => api.get("/wa-groups").then((r) => setData(r.data)).catch(() => {}), []);
  const loadNums = useCallback(() => api.get(`/wa-groups/${key}/numbers`).then((r) => setNums(r.data)).catch(() => {}), [key]);
  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadNums(); }, [loadNums]);
  if (user?.role !== "owner") { return <Navigate to="/inbox" replace />; }

  const refresh = () => { loadGroups(); loadNums(); };
  const done = (r) => { toast.success(`${r.data.added} added${r.data.skipped ? `, ${r.data.skipped} already in group` : ""}`); refresh(); };
  const run = async (fn) => { setBusy(true); let ok = false; try { done(await fn()); ok = true; } catch (e) { toast.error(apiError(e.response?.data?.detail)); } setBusy(false); return ok; };
  const addTyped = () => run(() => api.post(`/wa-groups/${key}/numbers`, { text })).then((ok) => { if (ok) { setText(""); } });
  const upload = (f) => { if (!f) { return; } const fd = new FormData(); fd.append("file", f); run(() => api.post(`/wa-groups/${key}/import`, fd)); };
  const remove = async (n) => { try { await api.delete(`/wa-groups/${key}/numbers/${n.id}`); refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const group = data.groups.find((g) => g.key === key);

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="wa-groups-page">
      <div>
        <p className="eyebrow text-gold/80">Company only</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold flex items-center gap-3"><MessageCircle className="w-8 h-8 text-[#25D366]" /> WhatsApp Groups</h1>
        <p className="text-sm text-emerald-100/60 mt-1">Each person gets the message privately from your business WhatsApp number. Groups fill automatically from the telecaller sheet, members and customers.</p>
        {!data.whatsapp_ready && <p className="mt-2 text-sm text-red-300" data-testid="wa-not-ready">WhatsApp (Gupshup) is not connected on this website yet — sending is off.</p>}
      </div>
      <WaStatus onTemplates={setTemplates} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wa-group-list">
        {data.groups.map((g) => (
          <button key={g.key} onClick={() => setKey(g.key)} data-testid={`wa-group-${g.key}`}
            className={`text-left rounded-xl border p-3 transition-colors ${key === g.key ? "border-[#25D366] bg-[#25D366]/10" : "border-gold/20 hover:border-gold/50 bg-white/[0.02]"}`}>
            <Users className="w-4 h-4 text-gold mb-1" />
            <p className="text-sm font-semibold leading-tight">{g.label}</p>
            <p className="text-xs text-emerald-100/60 mt-1" data-testid={`wa-group-count-${g.key}`}>{g.count} numbers</p>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="glass border-gold/20 p-4 space-y-3" data-testid="wa-numbers-card">
          <p className="font-serif font-semibold text-gold">{group?.label} · {nums.length}</p>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={"Type or paste numbers, one per line\nRahim 9876543210"} data-testid="wa-add-text" className="bg-card/60" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={addTyped} disabled={busy || !text.trim()} data-testid="wa-add-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90">Add numbers</Button>
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-gold/40 text-gold text-sm cursor-pointer" data-testid="wa-upload-label">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Excel / PDF
              <input type="file" accept=".xlsx,.xlsm,.csv,.pdf" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="wa-upload-input" />
            </label>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-border/40" data-testid="wa-number-list">
            {nums.map((n) => (
              <div key={n.phone} className="flex items-center gap-2 py-2 text-sm" data-testid={`wa-number-${n.phone}`}>
                <span className="flex-1 truncate">{n.name || "—"}</span>
                <span className="font-mono text-xs text-emerald-100/70">+{n.phone}</span>
                <span className="text-[10px] rounded-full border border-gold/30 px-2 py-0.5 text-gold/80">{n.source}</span>
                {n.manual ? <button onClick={() => remove(n)} data-testid={`wa-remove-${n.phone}`} className="p-1 text-red-300 hover:bg-white/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button> : <span className="w-5" />}
              </div>
            ))}
            {!nums.length && <p className="py-6 text-sm text-center text-emerald-100/50">No numbers in this group yet.</p>}
          </div>
          <p className="text-[11px] text-emerald-100/40">Auto numbers update by themselves; only numbers you added can be removed here.</p>
        </Card>
        <div className="space-y-6">
          <WaSendBox groupKey={key} label={group?.label} count={nums.length} templates={templates} disabled={!data.whatsapp_ready} onSent={() => setTick((t) => t + 1)} />
          <WaHistory tick={tick} />
        </div>
      </div>
    </div>
  );
}
